/**
 * Sentry error tracking initialization.
 *
 * The DSN is read from VITE_SENTRY_DSN at build time. If the variable is not
 * set, Sentry is disabled and errors are only logged to the browser console.
 *
 * To enable in production/staging:
 *   1. Create a project in Sentry for "abonamenty-literackie-frontend".
 *   2. Copy the DSN from Settings → Client Keys (DSN).
 *   3. Set VITE_SENTRY_DSN as a GitHub secret and pass it to the build workflow.
 */

import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'
const isProduction = environment === 'production'

export function initSentry() {
  if (!dsn) {
    console.info('[Sentry] DSN not configured; error tracking disabled.')
    return
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text and media by default to avoid recording PII
        // (names, addresses, emails) on a subscription/checkout site.
        maskAllText: true,
        blockAllMedia: true,
        // Unmask only elements explicitly marked as safe for debugging.
        unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
        ignore: ['.sentry-ignore', '[data-sentry-ignore]'],
      }),
    ],
    // Performance monitoring: sample 10% of transactions in production.
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    // Session replay sampling:
    // - Staging: record everything while debugging.
    // - Production: do not record normal sessions, but always record sessions
    //   that hit an error so we can reproduce crashes.
    replaysSessionSampleRate: isProduction ? 0 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    // Do not attach user info (email/name) to replays by default.
    dataCollection: {
      userInfo: false,
    },
    // Ignore noise from Android in-app browsers (Facebook/Instagram/Messenger).
    // These inject a JS bridge that throws when the native WebView is destroyed.
    ignoreErrors: [
      'Java object is gone',
      'Error invoking postMessage',
      'Internal error: Java object is gone',
    ],
    denyUrls: [
      // Android in-app browser injected scripts (iabjs://...)
      /^iabjs:\/\//,
    ],
    // Polish book-subscription site; useful for Sentry filtering.
    tags: {
      service: 'frontend',
    },
  })
}

export const SentryErrorBoundary = Sentry.ErrorBoundary

/**
 * Helper to manually capture exceptions (e.g., in catch blocks).
 */
export function captureException(error, context = {}) {
  if (!dsn) return
  Sentry.captureException(error, context)
}

/**
 * Helper to manually capture messages.
 */
export function captureMessage(message, level = 'info') {
  if (!dsn) return
  Sentry.captureMessage(message, level)
}
